import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { createResourceLocatorString } from '../../util/routes';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  parse,
  decodeLatLng,
  encodeLatLng,
  encodeLatLngBounds,
  decodeLatLngBounds,
  stringify,
} from '../../util/urlHelpers';
import routeConfiguration from '../../routeConfiguration';
import { Checkbox } from '../../components';

import css from './ProviderSelector.css';

const ProviderSelector = props => {
  const location = useLocation();
  const history = useHistory();
  const { providerId } = parse(location.search);
  const selectedIds = providerId ? providerId.split(',') : [];
  const { providers, value, pageName } = props;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProviders, setSelectedProviders] = useState(selectedIds);
  const intl = useIntl();
  const configCountryId = process.env.REACT_APP_COUNTRY_ID;

  const filteredProvidersUnsorted = providers.filter(provider =>
    provider.attributes.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const providersByCountry = filteredProvidersUnsorted.reduce((acc, provider) => {
    const cid = provider.attributes.countryId || 'unknown';
    if (!acc[cid]) acc[cid] = [];
    acc[cid].push(provider);
    return acc;
  }, {});

  const countryIds = Object.keys(providersByCountry);
  countryIds.sort((a, b) => {
    if (a === configCountryId) return -1;
    if (b === configCountryId) return 1;
    return a.localeCompare(b);
  });

  const filteredProviders = countryIds.flatMap(cid => providersByCountry[cid]);

  const clearProviderFilter = () => {
    const searchParams = {
      ...parse(location.search),
      providerId: undefined,
    };

    history.push(createResourceLocatorString(pageName, routeConfiguration(), {}, searchParams));

    setSelectedProviders([]);
    setIsModalOpen(false);
  };

  const onProvidersSubmit = e => {
    const providerMaybe = selectedProviders.length
      ? { providerId: selectedProviders.toString() }
      : {};
    const { providerId, ...restParams } = parse(location.search);

    const searchParams = {
      ...restParams,
      ...providerMaybe,
    };

    history.push(createResourceLocatorString(pageName, routeConfiguration(), {}, searchParams));

    setIsModalOpen(false);
  };

  const filterClasses = classNames(css.filtersBtn, { [css.borderFilters]: !!selectedIds.length });
  let lastCountryId = null;

  return (
    <div className={css.container}>
      <div className={css.filtersWrapper}>
        {!!selectedIds.length && <div className={css.filterDot}>{selectedIds.length}</div>}
        <button className={filterClasses} onClick={() => setIsModalOpen(true)}>
          <FormattedMessage id="FilterModal.provider" />
        </button>
      </div>

      {isModalOpen && (
        <Modal onClose={() => setIsModalOpen(false)}>
          <div className={css.modalContent}>
            <div className={css.modalHeader}>
              <h2>
                <FormattedMessage id="FilterModal.provider" />
              </h2>
              <input
                type="text"
                placeholder={intl.formatMessage({ id: 'MainFiltersForm.providerPlaceholder' })}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>

            <div className={css.providerContainer}>
              {filteredProviders.map(
                ({ attributes: { name, countryId }, id: { uuid }, distance }) => {
                  const isSelected = selectedProviders.includes(uuid);
                  const showSeparator = countryId !== lastCountryId && lastCountryId !== null;
                  lastCountryId = countryId;
                  return (
                    <React.Fragment key={uuid}>
                      {showSeparator && (
                        <div className={css.countrySeparator}>
                          <span>
                            <FormattedMessage id={`General-${countryId}`} />
                          </span>
                        </div>
                      )}
                      <div key={uuid} className={css.itemContainer}>
                        <Checkbox
                          label={name}
                          checked={isSelected}
                          size="large"
                          onChange={() => {
                            const updated = isSelected
                              ? selectedProviders.filter(id => id !== uuid)
                              : [...selectedProviders, uuid];
                            setSelectedProviders(updated);
                          }}
                        />
                        {(distance || distance === 0) && (
                          <span className={css.distanceText}>{distance} km</span>
                        )}
                      </div>
                    </React.Fragment>
                  );
                }
              )}
            </div>

            <div className={css.modalFooter}>
              <a className={css.clearLink} onClick={clearProviderFilter}>
                <FormattedMessage id="FilterModal.clear" defaultMessage="Wyczyść" />
              </a>
              <button className={css.btnSearchPro} onClick={onProvidersSubmit}>
                <FormattedMessage id="FilterModal.showResults" />
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

const Modal = ({ children, onClose }) => {
  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div className={css.modalBackdrop} onClick={onClose}>
      <div className={css.modalContainer} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>,
    document.body
  );
};

export default ProviderSelector;
